/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

export type EmailVerificationEventProperties = {
  /** Where the user entered the flow from -- the referring page on web, the calling screen on native. */
  flowSource: string;
  /** `compulsory` or `optional`: whether the account is usable before verifying. */
  flow: string;
  errorMessage?: string;
  submitStatus?: 'success' | 'fail';
};

export type EmailVerificationObservabilityTags = {
  flow: string;
  platform: 'web' | 'native';
  submitStatus?: 'success' | 'fail';
};

/**
 * Event names are carried over verbatim from the Redux-era `EmailVerificationEvents`
 * constants so the existing dashboards keep resolving after the unified page ships.
 */
abstract class EmailVerificationAnalyticsEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'performance';

  concept = CONCEPTS.people_and_pets.trust_and_safety;

  // Narrows the base class's `Record<string, unknown>` parameters to this page's
  // event shapes, so a missing or misspelled property fails to compile.
  // eslint-disable-next-line no-useless-constructor
  constructor(
    eventProperties: EmailVerificationEventProperties,
    observabilityTags: EmailVerificationObservabilityTags
  ) {
    super(eventProperties, observabilityTags);
  }
}

/** Fires once when the verification page mounts. */
export class VerifyEmailPageLoad extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-page-load';
}

/** Fires the first time the user types into the verification code field. */
export class VerifyEmailAddCode extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-add-code';
}

/**
 * Fires once the verify request settles, carrying the outcome. The submitted code is
 * deliberately left out: it is a single-use credential, and these events are stored
 * for `performance` analytics rather than held to a credential's lifetime.
 */
export class VerifyEmailSubmit extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-button-click';
}

/** Fires when the user asks for a fresh code. */
export class VerifyEmailResendCode extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-resend-code-button-click';
}

/** Fires when the user leaves the optional flow without verifying. */
export class VerifyEmailSkip extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-skip-button-click';
}

/** Fires when the user logs out of the compulsory flow instead of verifying. */
export class VerifyEmailLogout extends EmailVerificationAnalyticsEvent {
  eventName = 'email-verification-verify-logout-button-click';
}
